import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

export function useOfferGroups(params: ListParams = {}) {
  const { page = 1, pageSize = 50, search, status } = params;

  return useQuery({
    queryKey: ["offer-groups", { page, pageSize, search, status }],
    queryFn: async () => {
      const qp: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
      if (search) qp.search = search;
      if (status) qp.status = status;
      const res = await api.get("/v1/offer-groups", qp);
      if (!res.ok) throw new Error("Failed to fetch offer groups");
      return res.json();
    },
  });
}

export function useOfferGroup(id: number | string) {
  return useQuery({
    queryKey: ["offer-groups", id],
    queryFn: async () => {
      const res = await api.get(`/v1/offer-groups/${id}`);
      if (!res.ok) throw new Error("Failed to fetch offer group");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateOfferGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.post("/v1/offer-groups", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create offer group");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offer-groups"] });
    },
  });
}

export function useUpdateOfferGroup(id: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.patch(`/v1/offer-groups/${id}`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update offer group");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offer-groups"] });
    },
  });
}

export function useDeleteOfferGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api.delete(`/v1/offer-groups/${id}`);
      if (!res.ok) throw new Error("Failed to delete offer group");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offer-groups"] });
    },
  });
}
